/**
 * Admin → Clauses (OPS 3.3) — reusable proposal clause library. The composer
 * pulls COPIES of these into proposals; edits here never touch existing
 * proposals.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import type { ClauseLibraryItemOutput } from '@1financial/shared';

async function apiError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string } };
    return new Error(body?.error?.message ?? `Failed: ${res.status}`);
  } catch {
    return new Error(text || res.statusText || `Failed: ${res.status}`);
  }
}

const HDRS = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` });

export function ClauseLibraryPage() {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const canManage = canDo('clauses.manage');
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [deleting, setDeleting] = useState<ClauseLibraryItemOutput | null>(null);

  const list = useQuery({
    queryKey: ['clause-library', 'admin'],
    queryFn: async () => {
      const res = await fetch('/v1/clauses?all=1', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
      const body = await res.json() as { ok: true; data: { items: ClauseLibraryItemOutput[] } };
      return body.data.items;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['clause-library'] });
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/v1/clauses', { method: 'POST', headers: HDRS(), body: JSON.stringify({ title, body }) });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setAdding(false); setTitle(''); setBody(''); invalidate(); },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const res = await fetch(`/v1/clauses/${id}`, { method: 'PATCH', headers: HDRS(), body: JSON.stringify(patch) });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setEditingId(null); invalidate(); },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/clauses/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setDeleting(null); invalidate(); },
  });

  function move(c: ClauseLibraryItemOutput, dir: -1 | 1) {
    const items = list.data ?? [];
    const idx = items.findIndex((x) => x.id === c.id);
    const swap = items[idx + dir];
    if (!swap) return;
    void update.mutateAsync({ id: c.id, patch: { ordering: swap.ordering } })
      .then(() => update.mutateAsync({ id: swap.id, patch: { ordering: c.ordering } }))
      .then(invalidate);
  }

  const mutationError = create.error ?? update.error ?? remove.error;

  return (
    <div className="space-y-4 max-w-4xl">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Clause library</h2>
          <p className="text-sm text-muted">Reusable proposal clauses — composers pull editable copies; library edits never reach existing proposals.</p>
        </div>
        {canManage && (
          <button onClick={() => setAdding(true)} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">
            + New clause
          </button>
        )}
      </header>

      {mutationError && <div className="text-sm text-rose-300">{mutationError.message}</div>}

      {adding && (
        <div className="bg-bg-panel border border-border rounded-xl p-4 space-y-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Clause title" className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Clause body" className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button
              onClick={() => create.mutate()}
              disabled={!title.trim() || !body.trim() || create.isPending}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
            >
              Add clause
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(list.data ?? []).map((c, idx) => (
          <div key={c.id} className={`bg-bg-panel border border-border rounded-xl p-4 ${c.active ? '' : 'opacity-50'}`}>
            {editingId === c.id ? (
              <div className="space-y-2">
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
                <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={4} className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingId(null)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
                  <button
                    onClick={() => update.mutate({ id: c.id, patch: { title: editTitle, body: editBody } })}
                    disabled={!editTitle.trim() || !editBody.trim() || update.isPending}
                    className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <span className="text-xs text-muted w-6 pt-0.5">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">
                    {c.title}
                    {!c.active && <span className="ml-2 text-xs text-muted">(inactive)</span>}
                  </div>
                  <div className="text-xs text-muted mt-1 whitespace-pre-wrap break-words max-h-20 overflow-y-auto">{c.body}</div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => move(c, -1)} disabled={idx === 0} className="text-muted hover:text-white px-1 disabled:opacity-30" title="Move up">↑</button>
                    <button onClick={() => move(c, idx + 1 < (list.data?.length ?? 0) ? 1 : -1)} disabled={idx === (list.data?.length ?? 0) - 1} className="text-muted hover:text-white px-1 disabled:opacity-30" title="Move down">↓</button>
                    <button
                      onClick={() => { setEditingId(c.id); setEditTitle(c.title); setEditBody(c.body); }}
                      className="text-xs btn-ghost-info border px-2 py-1 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => update.mutate({ id: c.id, patch: { active: !c.active } })}
                      className="text-xs border border-border text-muted hover:text-white px-2 py-1 rounded"
                    >
                      {c.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => setDeleting(c)} className="text-xs btn-ghost-danger border px-2 py-1 rounded">Delete</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {list.data && list.data.length === 0 && (
          <div className="text-sm text-muted">No clauses yet — add the first one.</div>
        )}
      </div>

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md p-5 shadow-2xl animate-pop">
            <h3 className="text-base font-semibold text-white mb-2">Delete clause</h3>
            <p className="text-sm text-muted mb-4">Delete "{deleting.title}" from the library? Clause copies inside existing proposals are kept.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleting(null)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
              <button onClick={() => remove.mutate(deleting.id)} disabled={remove.isPending} className="bg-rose-700 hover:bg-rose-600 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
